import { GeoJSONFeature, MapMouseEvent, MapTouchEvent } from 'mapbox-gl';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Layer, Source } from 'react-map-gl/mapbox';
import usePublicBaseUrl from '@alltrails/context/hooks/usePublicBaseUrl';
import useGroupHover from '@alltrails/core/hooks/useGroupHover';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import { wrapUrlSafe } from '@alltrails/core/utils/wrapUrlSafe';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import { COLOR_TEXT_PRIMARY_INVERSE, FONT_SIZE_100, FONT_SIZE_50 } from '@alltrails/denali/tokens';
import type { AnonymizedActivity } from '@alltrails/types/Activity';
import { ActivityType } from '@alltrails/types/ActivityType';
import type { Visibility } from '@alltrails/types/Visibility';
import useMap from '../../hooks/useMap';
import { useAppDispatch } from '../../redux';
import { updateCursor } from '../../redux/reducer';
import { communityActivityClusterPinsId, defaultSymbolLayerLayoutProps, getLayerBeforeId } from '../../utils/layers';
import { createActivityPinClusterSvg, createActivityPinSvg } from '../../utils/svgDefinitions/createActivityPinSvg';
import { clusterPopupOffset, pinPopupOffset } from '../../utils/svgDefinitions/svgMarkerComponents';
import useSvgImages, { SvgDefinition } from '../../utils/useSvgImages';
import { MapLayerInteractionsContext } from '../MapLayerInteractionsProvider';
import CommunityActivityPopup from './CommunityActivityPopup';
import HoveredActivityPolylines from './HoveredActivityPolylines';

const apiKey = process.env.NEXT_PUBLIC_API_KEY!;

/**
 * Properties extracted from the community activity vector tile.
 */
export type CommunityActivityData = {
  activity: {
    uid: ActivityType;
    name: string;
  };
  anonymizedSlug: string;
  contentPrivacy: Visibility;
  createdAt: string;
  id: string;
  location?: {
    city: string;
    country: string;
    countryId: string;
    countryName: string;
    latitude: number;
    longitude: number;
    region: string;
    regionId: string;
    regionName: string;
  };
  name: string;
  photoCount: number;
  presentationType: 'track';
  rating: number;
  recordedAt: string;
  summaryStats: {
    calories?: number;
    distanceTotal: number;
    duration: number;
    elevationGain: number;
    elevationLoss?: number;
    elevationMax?: number;
    paceAverage?: number;
    speedAverage?: number;
    timeMoving?: number;
    timeTotal: number;
    updatedAt: string;
  };
};

type ActivityPayload = {
  activities: AnonymizedActivity[];
  activityCount?: number;
  clusterId?: number;
  lat: number;
  lng: number;
  polylines?: string[];
  stringifiedActivityIds: string[];
};

function getFeatureProperties(feature?: GeoJSONFeature): ActivityPayload | undefined {
  const properties = feature?.properties;
  if (!properties || feature.geometry.type !== 'Point') {
    return undefined;
  }

  const [lng, lat] = feature.geometry.coordinates;
  if (properties.point_count !== undefined) {
    const activities: AnonymizedActivity[] = (JSON.parse(properties.metadata_array) as CommunityActivityData[]).map(communityActivity => ({
      activityType: communityActivity.activity.uid,
      anonymizedSlug: communityActivity.anonymizedSlug,
      createdAt: communityActivity.createdAt,
      elevationGain: communityActivity.summaryStats.elevationGain,
      hasPhotos: communityActivity.photoCount > 0,
      id: Number(communityActivity.id),
      meters: communityActivity.summaryStats.distanceTotal,
      minutes: communityActivity.summaryStats.duration,
      name: communityActivity.name,
      contentPrivacy: communityActivity.contentPrivacy,
      locationName: communityActivity.location?.city ?? undefined,
      photoCount: communityActivity.photoCount
    }));

    const polylines: string[] = JSON.parse(properties.polylines);
    return {
      activities,
      activityCount: properties.point_count,
      clusterId: properties.cluster_id,
      lat,
      lng,
      polylines,
      stringifiedActivityIds: properties.activity_ids
    };
  }

  return undefined;
}

const activityPinImageName = 'activity-pin';
const activityPinHoveredImageName = 'activity-pin-hovered';
const activityPinClusterImageName = 'activity-pin-cluster';
const activityPinClusterHoveredImageName = 'activity-pin-cluster-hovered';
export const sourceId = 'community-activity-tiles';

const CommunityActivity = () => {
  const dispatch = useAppDispatch();
  const languageRegionCode = useLanguageRegionCode();
  const map = useMap();
  const isMobile = useIsMobileSizedScreen();
  const publicBaseUrl = usePublicBaseUrl();
  const { registerEventHandler, unregisterEventHandler } = useContext(MapLayerInteractionsContext);

  const [hovered, setHovered] = useState<ActivityPayload | undefined>(undefined);
  const [hoveredPolylineIndex, setHoveredPolylineIndex] = useState(0);
  const [selected, setSelected] = useState<ActivityPayload | undefined>(undefined);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const clusteredTileUrl = `${publicBaseUrl}/wmts/overlay/activityPinsClustered/{z}/{x}/{y}?key=${apiKey}`;

  const activePopup = selected ?? hovered;
  const activeIndex = selected ? selectedIndex : hoveredPolylineIndex;

  const hoveredEncodedPolyline = useMemo(() => {
    if (!activePopup) {
      return undefined;
    }
    if (activePopup.polylines?.length) {
      const index = Math.max(0, Math.min(activeIndex, activePopup.polylines.length - 1));
      return activePopup.polylines[index];
    }
    const first = activePopup.activities[0] as (AnonymizedActivity & { encoded_polyline?: string }) | undefined;
    return first?.encoded_polyline;
  }, [activePopup, activeIndex]);

  const svgDefinitions: SvgDefinition[] = useMemo(
    () => [
      createActivityPinSvg(activityPinImageName),
      createActivityPinSvg(activityPinHoveredImageName, true /* isHovered */),
      createActivityPinClusterSvg(activityPinClusterImageName),
      createActivityPinClusterSvg(activityPinClusterHoveredImageName, true /* isHovered */)
    ],
    []
  );
  const { imagesAreLoaded } = useSvgImages(svgDefinitions);

  const onActivityClusterClick = useCallback(
    (event: MapMouseEvent) => {
      if (!event.features?.length) {
        return;
      }

      const feature = event.features[0];
      const featureProperties = getFeatureProperties(feature);
      if (!featureProperties) {
        return;
      }

      event.preventDefault();

      if (featureProperties.activityCount === 1) {
        window.open(wrapUrlSafe(`/explore/recording/${featureProperties.activities[0].anonymizedSlug}`, languageRegionCode));
      } else {
        setSelected(featureProperties);
        setSelectedIndex(0);
      }
    },
    [languageRegionCode]
  );

  const onMobileClick = useCallback((event: MapMouseEvent) => {
    if (!event.features?.length) {
      return;
    }

    const feature = event.features[0];
    const featureProperties = getFeatureProperties(feature);
    if (!featureProperties) {
      return;
    }

    event.preventDefault();
    setSelected(featureProperties);
    setSelectedIndex(0);
  }, []);

  const onHover = useCallback(
    (payload?: ActivityPayload) => {
      if (!payload) {
        return;
      }
      setHovered(payload);
      setHoveredPolylineIndex(0);
      dispatch(updateCursor('pointer'));
    },
    [dispatch]
  );

  const onHoverEnd = useCallback(() => {
    setHovered(undefined);
    dispatch(updateCursor(''));
  }, [dispatch]);

  const { onMouseEnter, onMouseLeave } = useGroupHover(onHover, onHoverEnd, true /* isMouseMove */, 50);

  const onMouseMove = useCallback(
    (event: MapMouseEvent) => {
      if (!onMouseEnter || !event.features?.length) {
        return;
      }
      const feature = event.features[0];
      const featureProperties = getFeatureProperties(feature);
      if (!featureProperties) {
        return;
      }

      onMouseEnter(featureProperties);
    },
    [onMouseEnter]
  );

  useEffect(() => {
    if (isMobile) {
      registerEventHandler(communityActivityClusterPinsId, 'click', onMobileClick);
    } else {
      registerEventHandler(communityActivityClusterPinsId, 'click', onActivityClusterClick);
      registerEventHandler(communityActivityClusterPinsId, 'mousemove', onMouseMove);
      if (onMouseLeave) {
        registerEventHandler(communityActivityClusterPinsId, 'mouseleave', onMouseLeave);
      }
    }
    return () => {
      if (isMobile) {
        unregisterEventHandler(communityActivityClusterPinsId, 'click', onMobileClick);
      } else {
        unregisterEventHandler(communityActivityClusterPinsId, 'click', onActivityClusterClick);
        unregisterEventHandler(communityActivityClusterPinsId, 'mousemove', onMouseMove);
        if (onMouseLeave) {
          unregisterEventHandler(communityActivityClusterPinsId, 'mouseleave', onMouseLeave);
        }
      }
    };
  }, [isMobile, onActivityClusterClick, onMobileClick, onMouseLeave, onMouseMove, registerEventHandler, unregisterEventHandler]);

  useEffect(() => {
    if (!map) {
      return;
    }
    const onMapClick = (event: MapMouseEvent | MapTouchEvent) => {
      if (!event.defaultPrevented) {
        setSelected(undefined);
      }
    };
    const onMapMoveStart = () => setSelected(undefined);
    map.on('click', onMapClick);
    map.on('movestart', onMapMoveStart);
    return () => {
      map.off('click', onMapClick);
      map.off('movestart', onMapMoveStart);
    };
  }, [map]);

  if (!map || !imagesAreLoaded) {
    return null;
  }

  return (
    <>
      <Source id={sourceId} minzoom={3} tiles={[clusteredTileUrl]} type="vector">
        <Layer
          beforeId={getLayerBeforeId(map, communityActivityClusterPinsId)}
          filter={['has', 'point_count']}
          id={communityActivityClusterPinsId}
          source={sourceId}
          source-layer="activityPinsClustered"
          type="symbol"
          layout={{
            ...defaultSymbolLayerLayoutProps,
            'icon-image': [
              'case',
              ['==', ['get', 'point_count'], 1],
              [
                'case',
                ['==', ['get', 'activity_ids'], activePopup?.stringifiedActivityIds ?? null],
                activityPinHoveredImageName,
                activityPinImageName
              ],
              ['==', ['get', 'activity_ids'], activePopup?.stringifiedActivityIds ?? null],
              activityPinClusterHoveredImageName,
              activityPinClusterImageName
            ],
            'text-size': ['step', ['get', 'point_count'], FONT_SIZE_100, 100, FONT_SIZE_50, 1000, 8], // Reduce the font size at 100 and 1000 to avoid overflow
            'text-anchor': 'center',
            'text-field': ['case', ['==', ['get', 'point_count'], 1], '', ['get', 'point_count']]
          }}
          paint={{ 'text-color': COLOR_TEXT_PRIMARY_INVERSE }}
        />
      </Source>
      <HoveredActivityPolylines encodedPolyline={hoveredEncodedPolyline} />
      {activePopup && (
        <CommunityActivityPopup
          activities={activePopup.activities}
          index={activeIndex}
          latitude={activePopup.lat}
          longitude={activePopup.lng}
          offset={activePopup.activities.length > 1 ? clusterPopupOffset : pinPopupOffset}
          onIndexChange={selected ? setSelectedIndex : setHoveredPolylineIndex}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        />
      )}
    </>
  );
};

export default CommunityActivity;
